import React, {useEffect, useMemo, useState} from 'react';
import {
    Box, Stack, InputAdornment, SxProps, Typography, TypographyProps, IconButton,
    FormControl, InputLabel
} from '@mui/material';
import {alpha, useTheme} from '@mui/material/styles';
import {parseNumber} from '@/utils/dataUtils';
import {InputField} from '@/pages/components/common/InputField';

export interface MoneyDisplayProps {
    label?: string;
    value: number | string | null | undefined;
    /** whether to show 2 decimal places */
    showCents?: boolean;
    /** optional currency symbol prefix, e.g. $, ￥, HK$ */
    currencySymbol?: string;
    /** compact style: small font and tight spacing (default true) */
    compact?: boolean;
    /** text alignment: left | center | right (default 'right'). alignRight is deprecated. */
    align?: 'left' | 'center' | 'right';
    /** deprecated: use align instead */
    alignRight?: boolean;
    /** typography variant override */
    variant?: TypographyProps['variant'];
    /** color override */
    color?: TypographyProps['color'];
    /** sx override */
    sx?: SxProps;
    /** editable mode toggles between display and input */
    editable?: boolean;
    /** called with numeric value on blur/change */
    onChangeNumber?: (num: number) => void;
    /** called with numeric value during typing (onChange), before blur normalization */
    onChangeNumberImmediate?: (num: number) => void;
    /** called with display string (possibly formatted) on blur/change */
    onChangeDisplay?: (display: string) => void;
    /** placeholder for input */
    placeholder?: string;
    /** called when input receives focus */
    onFocus?: () => void;
    /** tiny mode: reduces padding when no label is present (default false) */
    tiny?: boolean;
    /** error state for validation */
    error?: boolean;
    /** helper text to show below the field (typically error message) */
    helperText?: string;
    /** separator string for split mode (default empty, when set enables split mode) */
    separator?: string;
    /** suffix value for split mode (default 0) */
    suffixValue?: number;
    /** icon component for toggling split mode display */
    icon?: React.ReactNode;
    /** icon component for canceling split mode (shown at the end of split mode view) */
    cancelIcon?: React.ReactNode;
    /** called when suffix value changes in split mode */
    onSuffixChange?: (num: number) => void;
    /** called when split mode is activated (edit icon clicked to open) */
    onEditStart?: () => void;
    /** called when split mode is canceled (cancel icon clicked) */
    onEditCancel?: () => void;
    /** force split mode to be shown (even when suffixValue is 0) */
    forceShowSplitMode?: boolean;
    /**
     * When true in non-editable split mode, render suffix as display text (no input).
     * Default false preserves left-display / right-editable split (e.g. Tax b/f override).
     */
    suffixReadOnly?: boolean;
    /**
     * Hover the calculated (left) value — split mode: left pane only; non-split: whole display.
     * Used by BankLoan Summary → RepaymentScheduleDrawer source tracing.
     */
    onCalcHoverChange?: (hovering: boolean) => void;
    /** Allow leading '-' / parentheses negative input. Default true. */
    allowNegative?: boolean;
    /** When true, reject decimal point and keep whole numbers only. Default false. */
    integerOnly?: boolean;
    /** Hover label tip content (forwarded to InputField). */
    popoverContent?: React.ReactNode;
}

function formatWithParen(n: number, formatter: Intl.NumberFormat, currencySymbol?: string): string {
    const abs = Math.abs(n);
    const formatted = formatter.format(abs);
    return n < 0 ? `(${formatted})` : formatted
}

export function MoneyField({
                               label = '',
                               value,
                               showCents = true,
                               currencySymbol,
                               compact = true,
                               align,
                               alignRight,
                               variant,
                               color,
                               sx,
                               editable = false,
                               onChangeNumber,
                               onChangeNumberImmediate,
                               onChangeDisplay,
                               placeholder,
                               onFocus: onFocusProp,
                               tiny = false,
                               error = false,
                               helperText,
                               separator = '',
                               suffixValue = 0,
                               icon,
                               cancelIcon,
                               onSuffixChange,
                               onEditStart,
                               onEditCancel,
                               forceShowSplitMode = false,
                               suffixReadOnly = false,
                               onCalcHoverChange,
                               allowNegative = true,
                               integerOnly = false,
                               popoverContent,
                           }: MoneyDisplayProps) {
    const theme = useTheme();
    // Backward compatibility: if align is not provided, derive from alignRight flag
    const resolvedAlign: 'left' | 'center' | 'right' = align ?? (alignRight === undefined ? 'left' : (alignRight ? 'right' : 'left'));
    const num = parseNumber(value);
    const formatter = useMemo(() => new Intl.NumberFormat(undefined, {
        minimumFractionDigits: showCents ? 2 : 0,
        maximumFractionDigits: showCents ? 2 : 0,
    }), [showCents]);
    const formatted = formatWithParen(num, formatter, currencySymbol);

    const [draft, setDraft] = useState<string>(typeof value === 'string' ? String(value) : formatted);
    const [isComposing, setIsComposing] = useState<boolean>(false);
    const [isFocused, setIsFocused] = useState<boolean>(false);
    // Sync ref so parent re-renders mid-keystroke cannot wipe draft before isFocused state commits
    const isFocusedRef = React.useRef(false);

    // Check if split mode is enabled (separator not empty)
    const isSplitMode = separator !== '';

    // Split mode state: initialize based on separator, suffixValue, or forceShowSplitMode
    // When separator is not empty AND (suffixValue != 0/null OR forceShowSplitMode), default to split mode
    const [showSplitMode, setShowSplitMode] = useState<boolean>(() => {
        return isSplitMode && (forceShowSplitMode || (suffixValue != null && suffixValue !== 0));
    });
    const [suffixDraft, setSuffixDraft] = useState<string>(String(suffixValue));
    const [suffixFocused, setSuffixFocused] = useState<boolean>(false);

    // Sync suffix draft with external suffixValue
    useEffect(() => {
        if (!suffixFocused) {
            setSuffixDraft(formatWithParen(suffixValue, formatter, undefined));
        }
    }, [suffixValue, formatter, suffixFocused]);

    // Update showSplitMode when suffixValue or forceShowSplitMode changes from external source
    useEffect(() => {
        if (isSplitMode && (forceShowSplitMode || (suffixValue != null && suffixValue !== 0))) {
            setShowSplitMode(true);
        }
    }, [isSplitMode, suffixValue, forceShowSplitMode]);

    // Keep internal draft in sync with external value/formatter changes
    useEffect(() => {
        // When external value changes, sync draft. Preserve user typing during composition / focus.
        if (isComposing || isFocused || isFocusedRef.current) return;
        const nextNum = parseNumber(value);
        const nextFormatted = formatWithParen(nextNum, formatter, currencySymbol);
        setDraft(typeof value === 'string' ? String(value) : nextFormatted);
    }, [value, formatter, isComposing, isFocused, currencySymbol]);

    // Handle suffix value focus
    const handleSuffixFocus = () => {
        setSuffixFocused(true);
        const n = parseNumber(suffixDraft);
        if (n === 0) {
            setSuffixDraft('');
        } else {
            setSuffixDraft(String(n));
        }
    };

    // Handle suffix value blur
    const handleSuffixBlur = () => {
        setSuffixFocused(false);
        const n = parseNumber(suffixDraft);
        const safe = Number.isFinite(n) ? n : 0;
        setSuffixDraft(formatWithParen(safe, formatter, undefined));
        onSuffixChange?.(safe);
    };

    // Handle suffix value change
    const handleSuffixChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value ?? '';
        if (raw === '') {
            setSuffixDraft('');
            return;
        }
        // Allow digits, decimal point, and negative sign
        let s = raw.replace(/,/g, '').trim();
        let neg = false;
        if (s.startsWith('-')) {
            neg = true;
            s = s.slice(1);
        }
        s = s.replace(/[^0-9.]/g, '');
        const firstDot = s.indexOf('.');
        if (firstDot >= 0) {
            s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
        }
        let [intPart, decPart] = s.split('.') as [string, string | undefined];
        if (showCents && decPart) decPart = decPart.slice(0, 2);
        const next = `${neg ? '-' : ''}${intPart}${firstDot >= 0 ? '.' : ''}${decPart ?? ''}`;
        setSuffixDraft(next);
    };

    // Handle icon click to toggle split mode
    const handleIconClick = () => {
        const next = !showSplitMode;
        setShowSplitMode(next);
        if (next && onEditStart) {
            onEditStart();
        }
    };

    // Handle cancel icon click to exit split mode and reset suffix value
    const handleCancelClick = () => {
        setShowSplitMode(false);
        setSuffixDraft(formatWithParen(0, formatter, undefined));
        // WHY: onEditCancel owns clear→null; trailing onSuffixChange(0) re-writes 0 and undoes delete handlers
        // (RepaymentScheduleDrawer instalment cancel, TaxWp, etc.).
        if (onEditCancel) {
            onEditCancel();
            return;
        }
        onSuffixChange?.(0);
    };

    if (!editable) {
        // When value is 0, always show "-" instead of formatted zero
        const displayText = num === 0 ? '-' : formatted;

        // Determine vertical alignment based on align prop
        const verticalAlign = resolvedAlign === 'center' ? 'center' : (resolvedAlign === 'right' ? 'flex-end' : 'flex-start');

        // Render split mode with FormControl + InputLabel (与 InputField br-suffix 保持一致的 floating label)
        if (isSplitMode && showSplitMode) {
            // Idle edit-icon chrome ~28px (browser measure). Shrink font/icon so active split stays flat.
            const splitH = compact ? 28 : undefined;
            const splitPadding = compact ? '2px 6px' : '8px 10px';
            const splitFontSize = compact ? '0.75rem' : ((theme.typography as any).body2?.fontSize || '0.875rem');
            const splitLineHeight = compact ? 1.15 : 1.2;
            const splitPaneBg = alpha(theme.palette.primary.main, 0.06);
            const compactHeightSx = compact
                ? {
                    height: splitH,
                    maxHeight: splitH,
                    minHeight: splitH,
                    boxSizing: 'border-box' as const,
                    '& > div': {
                        height: '100%',
                        maxHeight: '100%',
                        boxSizing: 'border-box' as const,
                        overflow: 'hidden',
                    },
                }
                : undefined;

            return (
                <FormControl
                    variant="outlined"
                    fullWidth
                    size={compact ? 'small' : 'medium'}
                    sx={{
                        padding: tiny ? '0px' : undefined,
                        ...compactHeightSx,
                        ...sx,
                    }}
                >
                    {label && (
                        <InputLabel
                            shrink
                            sx={{
                                backgroundColor: theme.palette.background.paper,
                                px: 0.5,
                            }}
                        >
                            {label}
                        </InputLabel>
                    )}
                    {/* Unified container for split mode - matches InputField br-suffix outlined design */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                            padding: 0,
                            backgroundColor: splitPaneBg,
                            overflow: 'hidden',
                            boxSizing: 'border-box',
                            height: '100%',
                            maxHeight: '100%',
                            '&:hover': {
                                borderColor: theme.palette.text.primary,
                            },
                            '&:focus-within': {
                                borderColor: theme.palette.primary.main,
                            },
                        }}
                    >
                        {/* Left part - readonly value (calc side — Summary hover traces here only) */}
                        <Box
                            onMouseEnter={onCalcHoverChange ? () => onCalcHoverChange(true) : undefined}
                            onMouseLeave={onCalcHoverChange ? () => onCalcHoverChange(false) : undefined}
                            sx={{
                                flex: '1 1 auto',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: resolvedAlign === 'center' ? 'center' : (resolvedAlign === 'right' ? 'flex-end' : 'flex-start'),
                                padding: splitPadding,
                                backgroundColor: splitPaneBg,
                                minWidth: 'max-content',
                                alignSelf: 'stretch',
                                boxSizing: 'border-box',
                                overflow: 'hidden',
                                cursor: onCalcHoverChange ? 'pointer' : undefined,
                            }}
                        >
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: splitFontSize,
                                    lineHeight: splitLineHeight,
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                    color: theme.palette.text.primary,
                                }}
                            >
                                {num === 0 ? displayText : (currencySymbol ? `${currencySymbol} ${displayText}` : displayText)}
                            </Typography>
                        </Box>
                        {/* Separator */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                px: 0.5,
                                fontWeight: 600,
                                fontSize: splitFontSize,
                                lineHeight: splitLineHeight,
                                color: theme.palette.text.primary,
                                userSelect: 'none',
                                backgroundColor: splitPaneBg,
                                alignSelf: 'stretch',
                            }}
                        >
                            {separator}
                        </Box>
                        {/* Right part — suffixReadOnly: display; else editable override input */}
                        <Box
                            sx={{
                                flex: '1 1 auto',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: resolvedAlign === 'center' ? 'center' : (resolvedAlign === 'right' ? 'flex-end' : 'flex-start'),
                                backgroundColor: splitPaneBg,
                                minWidth: 'max-content',
                                padding: splitPadding,
                                alignSelf: 'stretch',
                                boxSizing: 'border-box',
                                overflow: 'hidden',
                            }}
                        >
                            {suffixReadOnly ? (() => {
                                const suffixNum = parseNumber(suffixValue);
                                const suffixFormatted = formatWithParen(suffixNum, formatter, currencySymbol);
                                const suffixDisplayText = suffixNum === 0 ? '-' : suffixFormatted;
                                const suffixLabel = suffixNum === 0
                                    ? suffixDisplayText
                                    : (currencySymbol ? `${currencySymbol} ${suffixDisplayText}` : suffixDisplayText);
                                return (
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: splitFontSize,
                                            lineHeight: splitLineHeight,
                                            fontWeight: 500,
                                            whiteSpace: 'nowrap',
                                            color: theme.palette.text.primary,
                                        }}
                                    >
                                        {suffixLabel}
                                    </Typography>
                                );
                            })() : (
                                <input
                                    value={suffixDraft}
                                    onChange={handleSuffixChange}
                                    onFocus={handleSuffixFocus}
                                    onBlur={handleSuffixBlur}
                                    inputMode="decimal"
                                    size={Math.max(8, String(suffixDraft || '').length + 1)}
                                    style={{
                                        width: 'auto',
                                        minWidth: 48,
                                        border: 'none',
                                        outline: 'none',
                                        backgroundColor: 'transparent',
                                        padding: 0,
                                        margin: 0,
                                        fontSize: typeof splitFontSize === 'string' ? splitFontSize : '0.75rem',
                                        lineHeight: splitLineHeight,
                                        fontFamily: theme.typography.fontFamily as string,
                                        color: theme.palette.text.primary,
                                        textAlign: resolvedAlign,
                                    }}
                                />
                            )}
                        </Box>
                        {/* Cancel icon to exit split mode */}
                        {!suffixReadOnly && cancelIcon && (
                            <IconButton
                                size="small"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={handleCancelClick}
                                sx={{
                                    p: 0,
                                    borderRadius: 0,
                                    width: compact ? 18 : 28,
                                    height: '100%',
                                    maxHeight: '100%',
                                    minWidth: 0,
                                    minHeight: 0,
                                    flexShrink: 0,
                                    alignSelf: 'stretch',
                                    '& .MuiSvgIcon-root': {fontSize: compact ? 14 : 20},
                                }}
                            >
                                {cancelIcon}
                            </IconButton>
                        )}
                    </Box>
                </FormControl>
            );
        }

        // Idle split: no input border; value inherits parent typography; toggle icon on the right.
        // width 100% so align=right/center actually docks in the parent flex cell (inline-flex cannot).
        if (isSplitMode) {
            const idleText = num === 0 ? displayText : (currencySymbol ? `${currencySymbol} ${displayText}` : displayText);
            const idleValue = (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: resolvedAlign === 'center' ? 'center' : (resolvedAlign === 'right' ? 'flex-end' : 'flex-start'),
                        gap: 0.25,
                        minWidth: 0,
                        width: '100%',
                        ...(label ? undefined : sx),
                    }}
                >
                    <Box
                        component="span"
                        onMouseEnter={onCalcHoverChange ? () => onCalcHoverChange(true) : undefined}
                        onMouseLeave={onCalcHoverChange ? () => onCalcHoverChange(false) : undefined}
                        sx={{
                            fontSize: 'inherit',
                            fontWeight: 'inherit',
                            fontFamily: 'inherit',
                            lineHeight: 'inherit',
                            color: color ?? 'inherit',
                            whiteSpace: 'nowrap',
                            textAlign: resolvedAlign,
                            cursor: onCalcHoverChange ? 'pointer' : undefined,
                        }}
                    >
                        {idleText}
                    </Box>
                    {icon && (
                        <IconButton
                            size="small"
                            onClick={handleIconClick}
                            aria-label="toggle-split-mode"
                            sx={{p: 0.25, transform: 'scale(0.75)', transformOrigin: 'left center'}}
                        >
                            {icon}
                        </IconButton>
                    )}
                </Box>
            );
            if (!label) {
                return idleValue;
            }
            return (
                <Stack sx={{...sx}}>
                    <Typography align={resolvedAlign} color="#00000099" sx={{fontSize: 'inherit', lineHeight: 1.2}}>
                        {label}
                    </Typography>
                    {idleValue}
                </Stack>
            );
        }

        if (popoverContent) {
            return (
                <InputField
                    display
                    label={label}
                    value={num === 0 ? '-' : (currencySymbol ? `${currencySymbol} ${displayText}` : displayText)}
                    popoverContent={popoverContent}
                    compact={compact}
                    align={resolvedAlign}
                    onChange={() => {}}
                    sx={sx}
                />
            );
        }
        return (
            <Stack sx={{
                padding: tiny ? '0px' : 0.5,
                paddingLeft: label ? 1.5 : 0,
                paddingTop: tiny ? '0px' : undefined,
                paddingBottom: tiny ? '0px' : undefined,
                minHeight: tiny ? 'auto' : undefined,
                display: 'flex',
                justifyContent: verticalAlign,
                alignItems: resolvedAlign === 'center' ? 'center' : (resolvedAlign === 'right' ? 'flex-end' : 'flex-start'),
                ...sx
            }}>
                {label && <Typography align={resolvedAlign} color="#00000099" sx={{fontSize: 12}}>{label}</Typography>}
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                    <Typography
                        align={resolvedAlign}
                        component="span"
                        variant={variant || (compact ? 'body2' : 'body1')}
                        color={color}
                        onMouseEnter={onCalcHoverChange ? () => onCalcHoverChange(true) : undefined}
                        onMouseLeave={onCalcHoverChange ? () => onCalcHoverChange(false) : undefined}
                        sx={{
                            fontSize: 12,
                            fontWeight: 500,
                            display: 'inline-flex',
                            alignItems: 'center',
                            whiteSpace: 'nowrap',
                            gap: 0.25,
                            cursor: onCalcHoverChange ? 'pointer' : undefined,
                            textAlign: resolvedAlign,
                            ...(compact ? {lineHeight: 1.2} : {}),
                        }}
                    >
                        <Box component="span">
                            {num === 0 ? displayText : (currencySymbol ? `${currencySymbol} ${displayText}` : displayText)}
                        </Box>
                    </Typography>
                </Box>
            </Stack>
        );
    }

    const handleFocus = () => {
        if (!editable) return;
        isFocusedRef.current = true;
        setIsFocused(true);
        // Call external onFocus callback if provided
        onFocusProp?.();
        // Convert any parentheses/pretty format into raw editable negative form without thousand separators
        const n = parseNumber(draft || value);
        // If current numeric value is 0, clear input for fresh typing as per requirement
        if (n === 0) {
            setDraft('');
            onChangeDisplay?.('');
            return;
        }
        let raw = `${Math.abs(n)}`;
        const dot = raw.indexOf('.');
        if ((!showCents || integerOnly) && dot >= 0) {
            raw = raw.slice(0, dot);
        } else if (showCents && dot >= 0) {
            const [i, d] = raw.split('.') as [string, string | undefined];
            raw = d ? `${i}.${d.slice(0, 2)}` : i;
        }
        const edit = (allowNegative && n < 0 ? '-' : '') + raw;
        setDraft(edit);
        onChangeDisplay?.(edit);
    };

    const handleBlur = () => {
        isFocusedRef.current = false;
        setIsFocused(false);
        // Normalize to a valid number, support parentheses or minus input when allowNegative
        let s = (draft ?? '').replace(/,/g, '').trim();
        let isParenNegative = false;
        if (allowNegative && /^\(.*\)$/.test(s)) {
            isParenNegative = true;
            s = s.slice(1, -1);
        }
        let neg = false;
        if (s.startsWith('-')) {
            if (allowNegative) {
                neg = true;
            }
            s = s.slice(1);
        }
        s = integerOnly ? s.replace(/[^0-9]/g, '') : s.replace(/[^0-9.]/g, '');
        const firstDot = integerOnly ? -1 : s.indexOf('.');
        if (firstDot >= 0) s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
        let [i, d] = s.split('.') as [string, string | undefined];
        if (!i) i = '0';
        if (!integerOnly && showCents && d) d = d.slice(0, 2);
        const composed = `${(allowNegative && (neg || isParenNegative)) ? '-' : ''}${i}${firstDot >= 0 ? '.' : ''}${d ?? ''}`;
        const n = Number(composed === '' || composed === '-' || composed === '.' || composed === '-.' ? 0 : composed);
        const safeRaw = Number.isFinite(n) ? n : 0;
        const safe = integerOnly ? Math.trunc(Math.abs(safeRaw)) * (allowNegative && safeRaw < 0 ? -1 : 1)
            : (allowNegative ? safeRaw : Math.abs(safeRaw));
        const pretty = formatWithParen(safe, formatter, currencySymbol);
        setDraft(pretty);
        onChangeNumber?.(safe);
        onChangeDisplay?.(pretty);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Treat any typing as focused (covers edge cases where focus state lagged a parent re-render)
        isFocusedRef.current = true;
        const raw = e.target.value ?? '';
        // During composition (IME), do not sanitize heavily
        if (isComposing) {
            setDraft(raw);
            return;
        }
        // Allow temporary empty value to let user type from scratch
        if (raw === '') {
            setDraft('');
            onChangeDisplay?.('');
            onChangeNumberImmediate?.(0);
            return;
        }
        // Normalize: optional leading '-' / parentheses when allowNegative; digits; optional single '.' unless integerOnly
        let s = raw.replace(/,/g, '').trim();
        let isParenNegative = false;
        if (allowNegative && /^\(.*\)$/.test(s)) {
            isParenNegative = true;
            s = s.slice(1, -1);
        }
        let neg = false;
        if (s.startsWith('-')) {
            if (allowNegative) {
                neg = true;
            }
            s = s.slice(1);
        }
        s = integerOnly ? s.replace(/[^0-9]/g, '') : s.replace(/[^0-9.]/g, '');
        const firstDot = integerOnly ? -1 : s.indexOf('.');
        if (firstDot >= 0) {
            // remove subsequent dots
            s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
        }
        let [intPart, decPart] = s.split('.') as [string, string | undefined];
        if (!integerOnly && showCents && decPart) decPart = decPart.slice(0, 2);
        const next = `${(allowNegative && (neg || isParenNegative)) ? '-' : ''}${intPart}${firstDot >= 0 ? '.' : ''}${decPart ?? ''}`;
        setDraft(next);
        onChangeDisplay?.(next);
        onChangeNumberImmediate?.(parseNumber(next));
    };

    const handleCompositionStart = () => setIsComposing(true);
    const handleCompositionEnd = (e: React.CompositionEvent<HTMLInputElement>) => {
        setIsComposing(false);
        // Trigger change normalization once composition ends
        const target = e.target as HTMLInputElement;
        const fakeEvent = {target} as unknown as React.ChangeEvent<HTMLInputElement>;
        handleChange(fakeEvent);
    };

    return (
        <Box sx={{padding: tiny ? '0px' : undefined}}>
            <InputField
                label={label}
                value={draft}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                onCompositionStart={handleCompositionStart}
                onCompositionEnd={handleCompositionEnd}
                placeholder={placeholder}
                compact={compact}
                align={resolvedAlign}
                variant="outlined"
                error={error}
                helperText={helperText}
                popoverContent={popoverContent}
                inputProps={{
                    inputMode: integerOnly || !showCents ? 'numeric' : 'decimal',
                }}
                InputProps={{
                    startAdornment: currencySymbol ? (
                        <InputAdornment
                            position="start"
                            sx={{
                                mr: 0,
                                px: 0.1,
                                flexBasis: '20%',
                                maxWidth: '10%',
                                flexShrink: 0,
                                justifyContent: 'center',
                                alignItems: 'center',
                                '&.MuiInputAdornment-root': {display: 'inline-flex'},
                            }}
                        >
                            <Box component="span" sx={{fontSize: '0.80em'}}>{currencySymbol}</Box>
                        </InputAdornment>
                    ) : undefined,
                }}
                sx={sx}
            />
        </Box>
    );
}
